/**
 * Pill — v.1-D §4.2.
 *
 * Status / tone chip. Seven canonical tones from Pattern 18.9. The `live`
 * tone renders the pulsing red dot via `circa-pulse` (global.css). Pages must
 * NOT inline `<Pill tone="literal">`; tones come from state-machine TONE maps
 * or the documented semantic tiers (CLAUDE.md §9.2 V.23).
 */
import type { JSX, ReactNode } from 'react';
import { cn } from '../../lib/utils';

export type PillTone = 'neutral' | 'good' | 'warn' | 'bad' | 'info' | 'live' | 'dark';

export interface PillProps {
  tone?: PillTone;
  children: ReactNode;
  size?: 'sm' | 'md';
  dot?: boolean;
  className?: string;
}

const TONE_BG: Record<PillTone, string> = {
  neutral: 'var(--lightest-gray)',
  good:    'rgba(22, 163, 74, 0.10)',
  warn:    'rgba(245, 158, 11, 0.12)',
  bad:     'rgba(220, 38, 38, 0.10)',
  info:    'rgba(24, 99, 220, 0.10)',
  live:    'rgba(220, 38, 38, 0.12)',
  dark:    'var(--ink)',
};

const TONE_FG: Record<PillTone, string> = {
  neutral: 'var(--ink-muted)',
  good:    'var(--good)',
  warn:    'var(--warn)',
  bad:     'var(--bad)',
  info:    'var(--info)',
  live:    'var(--live)',
  dark:    'var(--canvas)',
};

export function Pill({
  tone = 'neutral',
  children,
  size = 'md',
  dot = false,
  className,
}: PillProps): JSX.Element {
  const padY = size === 'sm' ? 2 : 4;
  const padX = size === 'sm' ? 8 : 10;
  const fontSize = size === 'sm' ? 11 : 12;
  return (
    <span
      className={cn('mono', className)}
      style={{
        display:        'inline-flex',
        alignItems:     'center',
        gap:            6,
        padding:        `${padY}px ${padX}px`,
        background:     TONE_BG[tone],
        color:          TONE_FG[tone],
        borderRadius:   'var(--radius-pill)',
        fontSize,
        fontFamily:     'var(--font-mono)',
        textTransform:  'uppercase',
        letterSpacing:  '0.06em',
        lineHeight:     1,
        whiteSpace:     'nowrap',
      }}
    >
      {(dot || tone === 'live') && (
        <span
          aria-hidden
          style={{
            display:      'inline-block',
            width:        6,
            height:       6,
            borderRadius: '50%',
            background:   tone === 'live' ? 'var(--live)' : 'currentColor',
            animation:    tone === 'live' ? 'circa-pulse var(--duration-pulse) ease-in-out infinite' : undefined,
          }}
        />
      )}
      {children}
    </span>
  );
}
